/**
 * AccountTypeCard - Displays the user's primary account type with visual distinction.
 * 
 * Shows one of three account types:
 * - Platform Administrator (admin): Full platform access with administrative controls
 * - Agency Account (agency): Manage multiple clients with own Twilio account
 * - Business Owner (business_owner): Single business using Calie.ai-provisioned Twilio
 * 
 * Also displays workspace role and available features for the account type.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { Shield, Building2, Store, Check } from 'lucide-react';

type AccountType = 'admin' | 'agency' | 'business_owner';

interface RoleConfig {
  icon: typeof Shield;
  label: string;
  badgeClass: string;
  cardClass: string;
  description: string;
  features: string[];
}

const ROLE_CONFIG: Record<AccountType, RoleConfig> = {
  admin: {
    icon: Shield,
    label: 'Platform Administrator',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
    cardClass: 'border-purple-200 dark:border-purple-800',
    description: 'Full platform access with administrative controls',
    features: ['Twilio Subaccounts', 'Testing Suite', 'All Workspaces'],
  },
  agency: {
    icon: Building2,
    label: 'Agency Account',
    badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800',
    cardClass: 'border-indigo-200 dark:border-indigo-800',
    description: 'Manage multiple clients with your own Twilio account',
    features: ['Custom Twilio', 'Multi-client', 'White-label'],
  },
  business_owner: {
    icon: Store,
    label: 'Business Owner',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
    cardClass: 'border-blue-200 dark:border-blue-800',
    description: 'Single business using Calie.ai-provisioned Twilio',
    features: ['Managed Twilio', 'Campaign Management', 'Call Tracking'],
  },
};

export function AccountTypeCard() {
  const { accountType: authAccountType, memberships, activeTenantId, loading } = useAuth();

  if (loading) {
    return (
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="h-5 w-32 bg-muted animate-pulse rounded" />
          <div className="h-4 w-48 bg-muted animate-pulse rounded mt-2" />
        </CardHeader>
      </Card>
    );
  }

  // Map accountType from secure calie_account_types table to local display type
  const accountType: AccountType = authAccountType === 'platform_admin' 
    ? 'admin' 
    : authAccountType === 'agency' 
      ? 'agency' 
      : 'business_owner';

  const config = ROLE_CONFIG[accountType];
  const Icon = config.icon;

  // Get workspace role from memberships
  const workspaceRole = memberships.find(m => m.tenant_id === activeTenantId)?.role || 'member';

  return (
    <Card className={config.cardClass}>
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${config.badgeClass}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 flex-wrap">
              {config.label}
              <Badge variant="outline" className={config.badgeClass}>
                {accountType.replace('_', ' ')}
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">{config.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Workspace Role</p>
          <Badge variant="secondary" className="capitalize">{workspaceRole}</Badge>
        </div>
        
        <div>
          <p className="text-sm text-muted-foreground mb-2">Available Features</p>
          <div className="flex flex-wrap gap-2">
            {config.features.map((feature) => (
              <div 
                key={feature} 
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-muted"
              >
                <Check className="h-3 w-3 text-green-600" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
